import { LuSchool, LuDoorOpen } from "react-icons/lu";
import { FiUsers } from "react-icons/fi";
import { FaChildren } from "react-icons/fa6";
import { useMatch, useNavigate, useParams } from "@tanstack/react-router";
import { getCurrentProgramYear } from "@/utils/dates";
import { useLeaData } from "@/queries";

interface StatCard {
  icon: React.ReactNode;
  value: number;
  label: string;
  path?: string;
}

const statValueStyle: React.CSSProperties = {
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#0F172A",
  lineHeight: 1,
};

const statLabelStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  marginTop: "0.25rem",
};

const DashboardStats = () => {
  const leaMatch = useMatch({ from: "/$leaid", shouldThrow: false });
  const currentUserOrg = leaMatch?.context?.currentUserOrg;
  const currentOrgPreschools = leaMatch?.context?.currentOrgPreschools;
  const { leaid } = useParams({ from: "/$leaid" });
  const navigate = useNavigate();

  const esiidPath = currentUserOrg?.esiIdPath ?? "";
  const { data: leaData } = useLeaData(esiidPath);

  const schoolCount = currentOrgPreschools?.length ?? 0;
  const currentYear = getCurrentProgramYear();

  const classroomCount = leaData?.entities?.classrooms
    ? Object.values(leaData.entities.classrooms).filter((entity) =>
        entity.records?.some((r) => r.ProgramYear === currentYear)
      ).length
    : 0;
  const staffCount = leaData?.entities?.staff
    ? Object.values(leaData.entities.staff).filter((entity) =>
        entity.records?.some((r) => r.ProgramYear === currentYear)
      ).length
    : 0;
  const childrenCount = leaData?.entities?.children
    ? Object.values(leaData.entities.children).filter((entity) =>
        entity.records?.some((r) => r.ProgramYear === currentYear)
      ).length
    : 0;

  const stats: StatCard[] = [
    {
      icon: <LuSchool size={22} color="#003a5d" />,
      value: schoolCount,
      label: "Schools",
    },
    {
      icon: <LuDoorOpen size={22} color="#0d9488" />,
      value: classroomCount,
      label: "Classrooms",
      path: `/${leaid}/records/classroom`,
    },
    {
      icon: <FiUsers size={22} color="#d97706" />,
      value: staffCount,
      label: "Staff",
      path: `/${leaid}/records/staff`,
    },
    {
      icon: <FaChildren size={22} color="#059669" />,
      value: childrenCount,
      label: "Children",
      path: `/${leaid}/records/child`,
    },
  ];

  return (
    <div className="row g-3">
      {stats.map((stat, i) => (
        <div className="col-12 col-sm-6 col-xl-3" key={i}>
          <div
            className="d-flex align-items-center gap-3 shadow-sm border"
            onClick={
              stat.path ? () => navigate({ to: stat.path! }) : undefined
            }
            onKeyDown={
              stat.path
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate({ to: stat.path! });
                    }
                  }
                : undefined
            }
            role={stat.path ? "button" : undefined}
            tabIndex={stat.path ? 0 : undefined}
            aria-label={
              stat.path
                ? `View ${stat.label}: ${stat.value.toLocaleString()}`
                : undefined
            }
            style={{
              padding: "1rem",
              borderRadius: "0.5rem",
              backgroundColor: "#fff",
              cursor: stat.path ? "pointer" : "default",
              transition: "box-shadow 0.15s ease",
            }}
            onMouseEnter={undefined}
            onMouseLeave={undefined}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "0.5rem",
                backgroundColor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {stat.icon}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: stat.path ? "column" : "column-reverse",
              }}
            >
              {stat.path ? (
                <>
                  <div style={statValueStyle} aria-hidden="true">
                    {stat.value.toLocaleString()}
                  </div>
                  <div
                    className="text-muted"
                    style={statLabelStyle}
                    aria-hidden="true"
                  >
                    {stat.label}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-muted" style={statLabelStyle}>
                    {stat.label}
                  </div>
                  <div style={statValueStyle}>
                    {stat.value.toLocaleString()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardStats;
